import React, {useContext, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import useViewportWidth from "hooks/useViewportWidth";
import {InboxContext} from "routes/MessagesPage";
import {getGuest, getInboxData} from "redux/actions/guestsActions";
import {useFeatureFlags} from "utilities/featureUtil";
// Styles & icons
import {useTheme} from "../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../connectui/component/buildStyles";
// Components
import Container from "../../connectui/component/layout/Container";
import VerticalPanelLayout from "connectui/component/layout/VerticalPanelLayout";
import SvgIcon from "connectui/component/display/SvgIcon";
import ButtonIcon from "../../connectui/component/input/button/ButtonIcon";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
// Panels
import FilterSetDetailsPanel from "ui/component/inbox/panel/filtersets/FilterSetDetailsPanel";
import FilterSetsPanel from "ui/component/inbox/panel/filtersets/FilterSetsPanel";
import MessageThreadsPanel from "ui/component/inbox/panel/threads/MessageThreadsPanel";
import ConversationsPanel from "ui/component/inbox/panel/conversations/ConversationsPanel";
import FiltersPanel from "ui/component/inbox/panel/filterlist/FiltersPanel";
import GuestDetailsPanelWrapper from "../component/inbox/panel/guestdetails/GuestDetailsPanelWrapper";
import ICONS from "connectui/icons/iconMap";

const PANEL_STYLES = [
  {minWidth: "250px", maxWidth: "300px"}, // filter_sets
  {minWidth: "330px", maxWidth: "350px"}, // threads
  {flex: 2}, // chat
  {minWidth: "370px", maxWidth: "400px"}, // guest_details
];

const useStyles = (theme) =>
  buildStyles({
    outerContainer: {
      backgroundColor: theme.colors.white,
      width: "100%",
      height: "100vh",
      boxShadow: theme.shadows.xs,
      display: "flex",
      flexDirection: "row",
      overflow: "hidden",
    },
    iconBtn: {
      borderRadius: theme.spacing(2),
      cursor: "pointer",
      padding: 0,
      border: "none",
      backgroundColor: "transparent",
      marginTop: "3px",
      "&:hover": {backgroundColor: theme.colors.hoverGray},
    },
    iconBtnSelected: {
      border: "none",
      backgroundColor: theme.colors.gray100,
      borderRadius: theme.spacing(2),
      cursor: "pointer",
      padding: 0,
      "&:hover": {backgroundColor: theme.colors.hoverGray},
    },
  });

const Inbox = () => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {guest_id} = useParams();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {newInboxViewUnreadCounts} = useFeatureFlags();
  const width = useViewportWidth({useBreakpointLabels: true});
  const isLG = width === "lg" || width === "md";
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const {setAppliedFilters, filtersOpen, setFiltersOpen} =
    useContext(InboxContext);
  const loadingGuestDetails = useSelector(
    (state) => state.defaultReducer.loading,
  ).guest;
  const [showFilterSets, setShowFilterSets] = useState(true);
  const [selectedView, setSelectedView] = useState(null);
  const [hideGuestPanel, setHideGuestPanel] = useState(!isLG && showFilterSets);
  const guest = guests[guest_id];

  useEffect(() => {
    if (!isLG) {
      if (showFilterSets && !hideGuestPanel) {
        setHideGuestPanel(true);
      }
    }
  }, [width, showFilterSets, hideGuestPanel, isLG]);

  useEffect(() => {
    let isMounted = true;
    if (!loadingGuestDetails && guest_id) {
      if (!guest || !guest.metadata) {
        dispatch(
          getGuest(guest_id, () => {
            if (!isMounted) return;
            if (newInboxViewUnreadCounts) {
              dispatch(getInboxData());
            }
          }),
        );
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    guest_id,
    guest,
    loadingGuestDetails,
    dispatch,
    newInboxViewUnreadCounts,
  ]);

  const handleGuestClick = (guest) => {
    dispatch(getGuest(guest.guest_id));
  };

  const toggleGuestDetailsPanelOpen = () => {
    setHideGuestPanel((prev) => !prev);
    if (!isLG && showFilterSets) {
      setShowFilterSets((prev) => !prev);
    }
  };

  const toggleFilterSetsPanelOpen = () => {
    const isOpen = !!showFilterSets;
    setShowFilterSets((prev) => !isOpen);
    if (!isLG && isOpen && !hideGuestPanel) {
      setHideGuestPanel((prev) => !prev);
    }
  };

  const handleEditView = (filterSet) => {
    setSelectedView(filterSet);
    setShowFilterSets(false);
  };

  const handleCloseView = (keepViewsOpen) => {
    if (keepViewsOpen) {
      setShowFilterSets(true);
      if (!isLG) setHideGuestPanel(true);
    }
    setSelectedView(null);
  };

  const handleSaveView = (filters) => {
    const newView = {
      color: "",
      icon: "",
      description: "",
      name: "",
      queries: filters,
    };
    setAppliedFilters(filters);
    setFiltersOpen(false);
    setSelectedView(newView);
    setShowFilterSets(false);
  };

  const expandIcon = (
    <ButtonIcon
      icon={
        <SvgIcon
          component={ICONS["layout-sidebar-left"]}
          sx={{
            width: "28px",
            height: "28px",
            padding: "5px",
            color: showFilterSets
              ? theme.colors.textPrimary
              : theme.colors.textSecondary,
          }}
        />
      }
      className={showFilterSets ? classes.iconBtnSelected : classes.iconBtn}
      onClick={toggleFilterSetsPanelOpen}
    />
  );

  return (
    <Container className={classes.outerContainer}>
      <VerticalPanelLayout
        panels={[
          showFilterSets && !selectedView ? (
            <FilterSetsPanel
              expandIcon={showFilterSets ? expandIcon : null}
              customHeader={
                <FlexContainer
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{width: "100%"}}
                >
                  <Text variant="displayXS" weight="semibold">
                    {t("filter-sets-panel-header")}
                  </Text>
                  {showFilterSets ? expandIcon : null}
                </FlexContainer>
              }
              editView={handleEditView}
            />
          ) : null,
          !!selectedView ? (
            <FilterSetDetailsPanel
              view={selectedView}
              onClose={handleCloseView}
            />
          ) : filtersOpen ? (
            <FiltersPanel
              onClose={() => setFiltersOpen(false)}
              saveView={handleSaveView}
            />
          ) : (
            <MessageThreadsPanel
              onGuestClick={handleGuestClick}
              openFilters={() => setFiltersOpen(true)}
              expandIcon={!showFilterSets ? expandIcon : null}
            />
          ),
          <ConversationsPanel
            guestId={guest_id}
            panelOpen={!hideGuestPanel}
            togglePanelOpen={toggleGuestDetailsPanelOpen}
          />,
          !hideGuestPanel ? <GuestDetailsPanelWrapper /> : null,
        ]}
        panelStyles={PANEL_STYLES}
      />
    </Container>
  );
};

export default Inbox;
